declare global {
  interface Navigator {
    msSaveBlob?: (blob: any, defaultName?: string) => boolean;
  }
}

/**
 * Exports an array of JavaScript objects to a CSV format and triggers the file download.
 *
 * @param {string} filename - The name of the CSV file to be downloaded, with or without the `.csv` extension.
 * @param {Object[]} data - An array of objects representing the data to export. Each object corresponds to a row in the CSV.
 * @param {string[]} headersLabel - An array of strings representing the CSV column headers.
 *
 * @returns {void}
 *
 * @example
 * const data = [
 *   { name: 'Alice', age: 25, city: 'Paris' },
 *   { name: 'Bob', age: 30, city: 'Lyon' }
 * ];
 *
 * const headersLabel = ['Name', 'Age', 'City'];
 *
 * exportToCSV('users.csv', data, headersLabel);
 */
export function exportToCSV(
  filename: string,
  data: object[],
  headersLabel: string[]
): void {
  const csvContent: string[] = [];

  csvContent.push(headersLabel.join(';'));  // ; for French Excel users
  data.forEach((obj) => {
    const items = Object.values(obj);
    let row = items.join(';');
    csvContent.push(row);
  });

  downloadFile(filename, 'text/csv;charset=utf-8;', csvContent);
}


/**
 * Triggers the download of a file with the specified content and filename.
 *
 * @param {string} filename - The name of the file to be downloaded, with an appropriate file extension.
 * @param {string | undefined} type - The MIME type of the file (e.g., 'text/csv'). If undefined, defaults to a generic type.
 * @param {string[]} fileContent - An array of strings representing the file's content. Each array element is a line in the file.
 *
 * @returns {void}
 *
 * @example
 * const content = ['Name,Age,City', 'Alice,25,Paris', 'Bob,30,Lyon'];
 * downloadFile('users.csv', 'text/csv;charset=utf-8;', content);
 */
function downloadFile(
  filename: string,
  type: string | undefined,
  fileContent: string[]
): void {
  const BOM = '\uFEFF';  //F*cking Excel 
  const blob = new Blob([BOM + fileContent.join('\n')], { type });

  if (navigator.msSaveBlob) {
    navigator.msSaveBlob(blob, filename);
  } else {
    const link = document.createElement('a');
    if (link.download !== undefined) {
      // feature detection
      // Browsers that support HTML5 download attribute
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }
}
